import React, { useEffect, useRef, useState } from "react";
import { X, Send, Upload, Paperclip, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

const POSITION_META = {
  internship: { label: "Internship", roleLabel: "Year of Study", placeholder: "e.g. 3rd year, B.A. LL.B (Hons.)" },
  appointment: { label: "Appointment", roleLabel: "Years at the Bar", placeholder: "e.g. 1.5 years · enrolled 2023" },
};

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  college_or_firm: "",
  year: "",
  cover_letter: "",
};

const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_BYTES = 5 * 1024 * 1024;

export default function CareersApplicationModal({ open, onClose, positionType }) {
  const meta = POSITION_META[positionType] || POSITION_META.internship;
  const [form, setForm] = useState(INITIAL);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setForm(INITIAL);
      setResume(null);
      setSubmitted(false);
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      setError("Please upload a PDF or Word document.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("Resume must be 5 MB or smaller.");
      return;
    }
    setError("");
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resume) {
      setError("Please attach your résumé (PDF or Word, up to 5 MB).");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("position_type", positionType);
      fd.append("college_or_firm", form.college_or_firm);
      fd.append("year", form.year);
      fd.append("cover_letter", form.cover_letter);
      fd.append("resume", resume);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success("Your application has been submitted.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start sm:items-center justify-center px-3 py-6 overflow-y-auto"
      data-testid="careers-application-modal"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(10,17,40,0.78)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="w-full max-w-2xl flex flex-col"
        style={{ backgroundColor: "#F9F6F0", border: "1px solid #C5A059", boxShadow: "0 30px 80px rgba(0,0,0,0.45)" }}
      >
        <div className="flex items-start justify-between gap-4 px-6 sm:px-10 pt-8 pb-5" style={{ borderBottom: "1px solid #E2DCD0" }}>
          <div>
            <span className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.28em" }}>
              Applying for · {meta.label}
            </span>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-2" style={{ color: "#0A1128", fontWeight: 500, lineHeight: 1.1 }}>
              {submitted ? "Application received" : "Submit your application"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-2"
            style={{ color: "#64748B" }}
            aria-label="Close"
            data-testid="careers-modal-close"
          >
            <X size={20} strokeWidth={1.6} />
          </button>
        </div>

        {submitted ? (
          <div className="px-6 sm:px-10 py-10 flex flex-col items-start gap-5">
            <CheckCircle2 size={36} strokeWidth={1.4} style={{ color: "#7A2021" }} />
            <p className="text-base" style={{ color: "#334155", lineHeight: 1.7 }}>
              Thank you. Your application and résumé have been received. We respond only to
              applications that match an active opening, typically within two to three weeks.
            </p>
            <button type="button" className="btn-primary" onClick={onClose} data-testid="careers-success-close">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 sm:px-10 py-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="careers-application-form">
            <Field label="Full Name" required>
              <input className="field-input-box" name="name" value={form.name} onChange={onChange} required minLength={2} data-testid="careers-name" />
            </Field>
            <Field label="Email" required>
              <input className="field-input-box" type="email" name="email" value={form.email} onChange={onChange} required data-testid="careers-email-input" />
            </Field>
            <Field label="Phone" required>
              <input className="field-input-box" name="phone" value={form.phone} onChange={onChange} required minLength={6} data-testid="careers-phone" />
            </Field>
            <Field label={positionType === "internship" ? "Law School / College" : "Most Recent Firm / Chamber"} required={false}>
              <input
                className="field-input-box"
                name="college_or_firm"
                value={form.college_or_firm}
                onChange={onChange}
                data-testid="careers-college"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={meta.roleLabel} required={false}>
                <input
                  className="field-input-box"
                  name="year"
                  value={form.year}
                  onChange={onChange}
                  placeholder={meta.placeholder}
                  data-testid="careers-year"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Cover note" required>
                <textarea
                  className="field-input-box"
                  rows={4}
                  name="cover_letter"
                  value={form.cover_letter}
                  onChange={onChange}
                  required
                  minLength={10}
                  placeholder="Why this position, areas of interest, availability dates."
                  data-testid="careers-cover-letter"
                />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <span className="field-label">
                Résumé (PDF or Word, up to 5 MB) <span style={{ color: "#7A2021" }}>*</span>
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onFile}
                className="hidden"
                data-testid="careers-resume-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-between px-4 py-3"
                style={{ backgroundColor: "#FFFFFF", border: "1px dashed #C5A059", color: "#0A1128" }}
                data-testid="careers-resume-btn"
              >
                <span className="flex items-center gap-3 text-sm">
                  {resume ? <Paperclip size={16} strokeWidth={1.6} style={{ color: "#7A2021" }} /> : <Upload size={16} strokeWidth={1.6} style={{ color: "#7A2021" }} />}
                  {resume ? (
                    <span>
                      <strong style={{ fontWeight: 500 }}>{resume.name}</strong>{" "}
                      <span style={{ color: "#64748B" }}>· {(resume.size / 1024 / 1024).toFixed(2)} MB</span>
                    </span>
                  ) : (
                    <span style={{ color: "#64748B" }}>Click to upload your résumé</span>
                  )}
                </span>
                {resume && (
                  <span className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.18em" }}>
                    Change
                  </span>
                )}
              </button>
            </div>

            {error && (
              <div className="sm:col-span-2 text-sm" style={{ color: "#991B1B" }} data-testid="careers-error">
                {error}
              </div>
            )}

            <div className="sm:col-span-2 pt-3 flex items-center justify-between gap-4" style={{ borderTop: "1px solid #E2DCD0" }}>
              <p className="text-xs" style={{ color: "#64748B" }}>
                We respond only to applications that match an active opening.
              </p>
              <button type="submit" className="btn-primary" disabled={submitting} data-testid="careers-submit">
                {submitting ? "Submitting…" : "Submit Application"}
                <Send size={14} strokeWidth={2} />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="field-label">
        {label} {required && <span style={{ color: "#7A2021" }}>*</span>}
      </span>
      {children}
    </label>
  );
}
